import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";

import LedgerTransactionCards from "./components/LedgerTransactionCards";
import LedgerTransactionFilters from "./components/LedgerTransactionFilters";
import LedgerTransactionTable from "./components/LedgerTransactionTable";

import { formatINR } from "./data/dummyLedgerTransactionsData";
import "./LedgerTransactions.css";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already loaded or fallback
}

export default function LedgerTransactions() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const companyInfo = {
    name: selectedCompany?.name || selectedCompany?.Name || "DAZY TOURE TRAVEL'S",
    address: selectedCompany?.Address || selectedCompany?.address || "Sahakar Marg",
    gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "08AGCPS3005M1ZB",
    stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
  };

  const navState = location.state || {};

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(
    navState.accountId || ""
  );
  const [selectedMonth, setSelectedMonth] = useState(
    navState.monthKey || "2026-08"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingBalanceType, setOpeningBalanceType] = useState("Dr");
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [closingBalanceType, setClosingBalanceType] = useState("Dr");
  const [accountDetails, setAccountDetails] = useState({
    group: navState.group || "General",
    type: navState.type || "Asset",
  });

  const cleanAccName = (rawName) => {
    if (!rawName) return "";
    return String(rawName)
      .replace(/\s*\(\d+\)\s*$/, "")
      .replace(/\s*-\s*\d+$/, "")
      .trim();
  };

  // Fetch accounts list
  useEffect(() => {
    if (!companyId) return;
    const fetchAccounts = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/reports/ledger/accounts/${companyId}`);
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const mapped = res.data.data.map((item) => {
            const cName = cleanAccName(item.HEADName || item.HEAD_NAME || item.name);
            return {
              id: String(item.HEAD_CODE || item.id),
              code: String(item.HEAD_CODE || item.code),
              name: cName,
              label: cName,
              group: item.GroupName || item.PARENT_NAME || "General",
              type: "Asset",
              openingBalance: Number(item.OpeningBal || item.OPENING_BAL || 0),
              balanceType: item.OpenBlType || item.DR_CR || "Dr",
            };
          });
          setAccounts(mapped);
          if (!selectedAccountId && mapped.length > 0) {
            setSelectedAccountId(mapped[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch accounts:", err);
      }
    };
    fetchAccounts();
  }, [BASE_API_URL, companyId]);

  const selectedAccount = useMemo(() => {
    return accounts.find((a) => (a.id || a.code) === selectedAccountId) || accounts[0] || null;
  }, [accounts, selectedAccountId]);

  const cleanAccountName = useMemo(() => {
    return cleanAccName(selectedAccount?.label || selectedAccount?.name || "");
  }, [selectedAccount]);

  const safeAccountName = useMemo(() => {
    return cleanAccountName
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/__+/g, "_")
      .replace(/^_|_$/g, "");
  }, [cleanAccountName]);

  const monthLabel = useMemo(() => {
    if (navState.monthLabel && selectedMonth === navState.monthKey) {
      return navState.monthLabel;
    }
    if (!selectedMonth) return "";
    const [y, m] = selectedMonth.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleString("en-US", { month: "short", year: "numeric" });
  }, [selectedMonth, navState]);

  // Load statement/transactions from API
  const loadTransactions = useCallback(async () => {
    if (!companyId || !selectedAccountId || !selectedMonth) return;
    setLoading(true);
    try {
      const [y, m] = selectedMonth.split("-");
      const lastDay = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
      const fromDate = `${selectedMonth}-01`;
      const toDate = `${selectedMonth}-${String(lastDay).padStart(2, "0")}`;

      const res = await axios.get(`${BASE_API_URL}/reports/ledger/statement/${companyId}`, {
        params: {
          headCode: selectedAccountId,
          fromDate,
          toDate,
        },
      });

      if (res.data?.success && res.data.data) {
        const data = res.data.data;

        setOpeningBalance(data.openingBalance?.amount || 0);
        setOpeningBalanceType(data.openingBalance?.type || "Dr");

        setClosingBalance(data.closingBalance?.amount || 0);
        setClosingBalanceType(data.closingBalance?.type || "Dr");

        setTotalDebit(data.summary?.totalDebit || 0);
        setTotalCredit(data.summary?.totalCredit || 0);

        if (data.account) {
          setAccountDetails({
            group: data.account.groupName || "General",
            type: "Asset",
          });
        }

        if (data.transactions && data.transactions.length > 0) {
          const mappedTxs = data.transactions.map((t, idx) => ({
            sNo: idx + 1,
            voucherId: t.voucherId || t.VoucherID || null,
            date: t.voucherDate || t.date,
            voucherNo: t.voucherNo || t.voucherNo,
            voucherType: t.voucherType || "Journal",
            particulars: t.particular || t.particulars || "Account Transaction",
            narration: t.narration || "",
            referenceNo: t.referenceNo || "-",
            debit: Number(t.debit || 0),
            credit: Number(t.credit || 0),
            balance: Number(t.balance || 0),
            balanceType: t.balanceType || "Dr",
          }));
          setTransactions(mappedTxs);
        } else {
          setTransactions([]);
        }
      } else {
        setTransactions([]);
        setOpeningBalance(0);
        setOpeningBalanceType("Dr");
        setTotalDebit(0);
        setTotalCredit(0);
        setClosingBalance(0);
        setClosingBalanceType("Dr");
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      setTransactions([]);
      showToast.error("Failed to load ledger transactions");
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, selectedAccountId, selectedMonth]);

  useEffect(() => {
    if (selectedAccountId && selectedMonth) {
      loadTransactions();
    }
  }, [selectedAccountId, selectedMonth, loadTransactions]);

  const handleShowTransactions = () => {
    loadTransactions();
    showToast.info(`Showing transactions for ${monthLabel}`);
  };

  const handleBack = () => {
    navigate("/monthly-ledger-summary");
  };

  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Filter transactions by search term
  const filteredTransactions = useMemo(() => {
    if (!searchTerm.trim()) return transactions;
    const term = searchTerm.toLowerCase();
    return transactions.filter(
      (tx) =>
        (tx.voucherNo && tx.voucherNo.toLowerCase().includes(term)) ||
        (tx.particulars && tx.particulars.toLowerCase().includes(term)) ||
        (tx.narration && tx.narration.toLowerCase().includes(term)) ||
        (tx.referenceNo && tx.referenceNo.toLowerCase().includes(term)) ||
        (tx.voucherType && tx.voucherType.toLowerCase().includes(term))
    );
  }, [transactions, searchTerm]);

  // Export to Excel - Exactly matching GST Tax Register formal structure
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "LEDGER TRANSACTIONS REPORT";
      const periodSubtitle = `Account: ${cleanAccountName}  |  Period: ${monthLabel}  |  Group: ${accountDetails.group}  |  As on: ${asOnTime}`;

      const headers = [
        "S.No.",
        "Date",
        "Voucher No.",
        "Voucher Type",
        "Particulars / Narration",
        "Reference No.",
        "Debit (INR)",
        "Credit (INR)",
        "Balance (INR)",
      ];

      const isOpDr = openingBalanceType === "Dr" && openingBalance > 0;
      const isOpCr = openingBalanceType === "Cr" && openingBalance > 0;

      const openingRow = [
        "",
        "Opening Balance",
        "",
        "",
        "",
        "",
        isOpDr ? openingBalance : "-",
        isOpCr ? openingBalance : "-",
        "-",
      ];

      const dataRows = transactions.map((t) => [
        t.sNo,
        t.date,
        t.voucherNo,
        t.voucherType,
        t.narration ? `${t.particulars} - ${t.narration}` : t.particulars,
        t.referenceNo || "-",
        t.debit > 0 ? t.debit : 0,
        t.credit > 0 ? t.credit : 0,
        `${formatINR(t.balance)} ${t.balanceType || "Dr"}`,
      ]);

      const totalRow = [
        "",
        "",
        "",
        "",
        "Total",
        "",
        totalDebit,
        totalCredit,
        `${formatINR(closingBalance)} ${closingBalanceType || "Dr"}`,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        openingRow,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      const lastCol = headers.length - 1;
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: lastCol } },
      ];

      worksheet["!cols"] = [
        { wch: 7 },  // S.No
        { wch: 14 }, // Date
        { wch: 14 }, // Voucher No
        { wch: 14 }, // Voucher Type
        { wch: 32 }, // Particulars
        { wch: 16 }, // Reference No
        { wch: 16 }, // Debit
        { wch: 16 }, // Credit
        { wch: 18 }, // Balance
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `Ledger_${monthLabel.replace(/\s+/g, "_")}`);
      const fileName = safeAccountName
        ? `Ledger_Transactions_${safeAccountName}_${selectedMonth}.xlsx`
        : `Ledger_Transactions_${selectedMonth}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF - Exactly matching GST Tax Register formal structure
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ");
      const reportTitle = "LEDGER TRANSACTIONS REPORT";

      // 1. Company Name Header (Centered, Bold, 15pt)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title Underlined
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Solid formal divider line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Account: ${cleanAccountName}   |   Group: ${accountDetails.group}   |   Type: ${accountDetails.type}`, 14, 36);
      doc.text(`Period: ${monthLabel}   |   Date: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Solid formal divider line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const isOpDr = openingBalanceType === "Dr" && openingBalance > 0;
      const isOpCr = openingBalanceType === "Cr" && openingBalance > 0;

      const tableRows = [
        [
          "",
          "Opening Balance",
          "",
          "",
          "",
          "",
          isOpDr ? formatINR(openingBalance) : "-",
          isOpCr ? formatINR(openingBalance) : "-",
          "-",
        ],
        ...transactions.map((t) => [
          t.sNo,
          t.date,
          t.voucherNo,
          t.voucherType,
          t.narration ? `${t.particulars}\n${t.narration}` : t.particulars,
          t.referenceNo || "-",
          t.debit > 0 ? formatINR(t.debit) : "-",
          t.credit > 0 ? formatINR(t.credit) : "-",
          `${formatINR(t.balance)} ${t.balanceType || "Dr"}`,
        ]),
        [
          "",
          "",
          "",
          "",
          "Total",
          "",
          formatINR(totalDebit),
          formatINR(totalCredit),
          `${formatINR(closingBalance)} ${closingBalanceType || "Dr"}`,
        ],
      ];

      autoTable(doc, {
        head: [
          [
            "S.No.",
            "Date",
            "Voucher No.",
            "Voucher Type",
            "Particulars / Narration",
            "Reference No.",
            "Debit (INR)",
            "Credit (INR)",
            "Balance (INR)",
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72], // --primary-teal
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8,
          halign: "center",
        },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 14 },
          1: { halign: "left", cellWidth: 24 },
          2: { halign: "left", cellWidth: 24 },
          3: { halign: "left", cellWidth: 22 },
          4: { halign: "left" },
          5: { halign: "left", cellWidth: 24 },
          6: { halign: "right", cellWidth: 26 },
          7: { halign: "right", cellWidth: 26 },
          8: { halign: "right", cellWidth: 28 },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text("etour - Design & Develop by Response Infoway", 14, doc.internal.pageSize.getHeight() - 8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
        },
      });

      const fileName = safeAccountName
        ? `Ledger_Transactions_${safeAccountName}_${selectedMonth}.pdf`
        : `Ledger_Transactions_${selectedMonth}.pdf`;
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="lt_page_wrapper">
      {/* Formal Printable Header (Visible during Print only) */}
      <div className="lt_print_header d-none d-print-block text-center mb-3">
        <h2 className="mb-0 fw-bold text-uppercase" style={{ fontSize: "18px", color: "#000000", letterSpacing: "0.5px" }}>
          {companyInfo.name}
        </h2>
        <div style={{ fontSize: "11px", color: "#333333", marginTop: "2px" }}>
          {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
        </div>
        <div className="fw-bold mt-2" style={{ fontSize: "13px", color: "#000000" }}>
          LEDGER TRANSACTIONS REPORT
        </div>
        <div style={{ fontSize: "10.5px", color: "#555555" }}>
          Account: {cleanAccountName} | Period: {monthLabel} | Group: {accountDetails.group} | As on: {asOnTime}
        </div>
      </div>

      {/* Top Header & Breadcrumb */}
      <div className="lt_header_section">
        <div className="lt_breadcrumb_bar">
          Reports <span>&gt;</span> <strong>Ledger Transactions</strong>
        </div>
        <div className="lt_as_on_badge">
          <i className="fa-regular fa-clock"></i>
          As on: {asOnTime}
        </div>
      </div>

      {/* Filter Card */}
      <LedgerTransactionFilters
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        onChangeAccount={setSelectedAccountId}
        selectedMonth={selectedMonth}
        onChangeMonth={setSelectedMonth}
        group={accountDetails.group}
        type={accountDetails.type}
        openingBalance={openingBalance}
        openingType={openingBalanceType}
        onShowTransactions={handleShowTransactions}
        onBack={handleBack}
      />

      {/* KPI Cards (3 in a row) */}
      <LedgerTransactionCards
        monthLabel={monthLabel}
        totalDebit={totalDebit}
        totalCredit={totalCredit}
        closingBalance={closingBalance}
        closingType={closingBalanceType}
      />

      {/* Transactions Table */}
      <LedgerTransactionTable
        transactions={transactions}
        monthLabel={monthLabel}
        openingBalance={openingBalance}
        openingType={openingBalanceType}
        totalDebit={totalDebit}
        totalCredit={totalCredit}
        closingBalance={closingBalance}
        closingType={closingBalanceType}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        onPrint={handlePrint}
        loading={loading}
      />

      {/* DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="lt_print_area d-none">
        {/* Company Header */}
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "8px", marginBottom: "10px" }}>
          <h2 style={{ margin: "0 0 3px 0", fontSize: "19px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000" }}>
            LEDGER TRANSACTIONS REPORT
          </div>
        </div>

        {/* Print Filter Meta */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Account:</strong> {cleanAccountName}  |  <strong>Group:</strong> {accountDetails.group}  |  <strong>Period:</strong> {monthLabel}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}  |  <strong>Records:</strong> {filteredTransactions.length}
          </div>
        </div>

        {/* Table */}
        <table className="lt_print_table">
          <thead>
            <tr>
              <th style={{ width: "5%", textAlign: "center" }}>S.No.</th>
              <th style={{ width: "9%", textAlign: "center" }}>Date</th>
              <th style={{ width: "11%", textAlign: "center" }}>Voucher No.</th>
              <th style={{ width: "9%", textAlign: "center" }}>Type</th>
              <th style={{ width: "32%" }}>Particulars / Narration</th>
              <th style={{ width: "11%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "11%", textAlign: "right" }}>Credit (₹)</th>
              <th style={{ width: "12%", textAlign: "right" }}>Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="print_opening_row">
              <td colSpan={4} style={{ fontWeight: 700 }}>Opening Balance</td>
              <td>-</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                {openingBalanceType === "Dr" && openingBalance > 0 ? formatINR(openingBalance) : "-"}
              </td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                {openingBalanceType === "Cr" && openingBalance > 0 ? formatINR(openingBalance) : "-"}
              </td>
              <td style={{ textAlign: "right" }}>-</td>
            </tr>
            {filteredTransactions.map((t, idx) => (
              <tr key={t.id || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td style={{ textAlign: "center" }}>{t.date}</td>
                <td style={{ textAlign: "center", fontWeight: 600 }}>{t.voucherNo}</td>
                <td style={{ textAlign: "center" }}>{t.voucherType}</td>
                <td>
                  <div>{t.particulars}</div>
                  {t.narration && (
                    <div style={{ fontSize: "9px", fontStyle: "italic", color: "#555" }}>
                      Narration: {t.narration}
                    </div>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>{t.debit > 0 ? formatINR(t.debit) : "-"}</td>
                <td style={{ textAlign: "right" }}>{t.credit > 0 ? formatINR(t.credit) : "-"}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatINR(t.balance)} {t.balanceType || "Dr"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td colSpan={5} style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totalDebit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>{formatINR(totalCredit)}</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(closingBalance)} {closingBalanceType || "Dr"}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="lt_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
